import { ActionContext, ActionTree, GetterTree, MutationTree, Store } from "vuex";
import { v4 as uuidv4 } from "uuid";
import { format } from "date-fns";
import { ManuscriptClient, DisplayOrderClient, ManuscriptContentPayload } from "@/lib/clients";
import {
  CountType,
  Manuscript,
  ManuscriptContentStatus,
  SearchTextIndex,
  ShowFlags,
  Theme,
} from "@/lib/models/manuscript";
import { db, GeneralSettings as GeneralSettingsDexie } from "@/lib/indexeddb";
import { UpdateOrder } from "@/lib/models/sortOrder";
import { SharedLinkKind, ManuscriptFolder, CreateManuscriptFolder, UpdateManuscriptFolder } from "@/lib/models";
import { stripSoftBreakMarker } from "@/lib/utils/scriptReflow";
import { mapWithConcurrency } from "@/lib/utils/mapWithConcurrency";

/** バックグラウンド本文取得の同時実行数 */
const BACKGROUND_LOAD_CONCURRENCY = 3;

const manuscriptClient = new ManuscriptClient();
const displayOrderClient = new DisplayOrderClient();

// 優先取得（エディタを開いた原稿）とバックグラウンド一括取得が同じキーを
// 同時に取得しようとするのを防ぐため、キーごとの取得中Promiseを保持する。
const inFlightContentLoads = new Map<string, Promise<void>>();

// Stateの型定義
export interface ManuscriptState {
  manuscriptList: Manuscript[];
  createdManuscriptKey: string;
  theme: Theme;
  countType: CountType;
  isShowPreviewOnManuscriptEditor: boolean;
  showFlags: ShowFlags;
  scrollRatio: number;
  autoSave: boolean;
  autoIndent: boolean;
  isFirstTimeAutoIndent: boolean;
  isShowingAutoIndentFirstTimeMessage: boolean;
  isShowEditorController: boolean;
  enabledSearchReplace: boolean;
  isShowAssistFab: boolean;
  enabledSearch: boolean;
  enabledReplace: boolean;
  enabledReplaceSingle: boolean;
  enabledReplaceAll: boolean;
  searchText: string;
  replaceText: string;
  searchTargetIndex: number;
  searchTotalCount: number;
  searchTextIndexList: SearchTextIndex[];
  baseWordCount: number;
  restoredContent: string | null;
  progressPercentage: boolean;
  manuscriptFolderList: ManuscriptFolder[];
  isExportCompleted: boolean;
  isExportCancelled: boolean;
  isSaved: boolean;
  hasShownWordLimitErrorDialog: boolean;
  isVerticalWritingMode: boolean;
  // 脚本モード専用の縦書きモード。通常モードの isVerticalWritingMode とは独立して管理する（デフォルト ON）
  isScriptVerticalWritingMode: boolean;
  /** 本文のバックグラウンド一括取得の進捗状態 */
  contentLoadState: "idle" | "loading" | "loaded";
  /**
   * initialize / cancelContentLoading のたびに増分するトークン。
   * バックグラウンド取得ループはこれが変わったら自身を中断する（作品切替・画面離脱時のキャンセル用）。
   */
  loadGeneration: number;
  /** 編集中・未保存の原稿キー一覧。バックグラウンド取得結果での上書きを防ぐガードに使う */
  editingManuscriptKeys: string[];
}

type Getters = {
  manuscript(state: ManuscriptState): (id: string) => Manuscript | undefined;
  manuscriptList(state: ManuscriptState): Manuscript[];
  createdManuscriptKey(state: ManuscriptState): string;
  theme(state: ManuscriptState): Theme;
  countType(state: ManuscriptState): CountType;
  isShowPreviewOnManuscriptEditor(state: ManuscriptState): boolean;
  showFlags(state: ManuscriptState): ShowFlags;
  scrollRatio(state: ManuscriptState): number;
  autoSave(state: ManuscriptState): boolean;
  autoIndent(state: ManuscriptState): boolean;
  isShowEditorController(state: ManuscriptState): boolean;
  isShowingAutoIndentFirstTimeMessage(state: ManuscriptState): boolean;
  enabledSearchReplace(state: ManuscriptState): boolean;
  isShowAssistFab(state: ManuscriptState): boolean;
  enabledSearch(state: ManuscriptState): boolean;
  enabledReplace(state: ManuscriptState): boolean;
  enabledReplaceSingle(state: ManuscriptState): boolean;
  enabledReplaceAll(state: ManuscriptState): boolean;
  searchText(state: ManuscriptState): string;
  replaceText(state: ManuscriptState): string;
  searchTargetIndex(state: ManuscriptState): number;
  searchTotalCount(state: ManuscriptState): number;
  searchTextIndexList(state: ManuscriptState): SearchTextIndex[];
  baseWordCount(state: ManuscriptState): number;
  restoredContent(state: ManuscriptState): string | null;
  progressPercentage(state: ManuscriptState): boolean;
  manuscriptFolder(state: ManuscriptState): (folderId: string) => ManuscriptFolder | undefined;
  manuscriptFolderList(state: ManuscriptState): ManuscriptFolder[];
  foldersBelongManuscript(state: ManuscriptState): (manuscriptKey: string) => ManuscriptFolder[];
  isExportCompleted(state: ManuscriptState): boolean;
  isExportCancelled(state: ManuscriptState): boolean;
  isSaved(state: ManuscriptState): boolean;
  hasShownWordLimitErrorDialog(state: ManuscriptState): boolean;
  isVerticalWritingMode(state: ManuscriptState): boolean;
  isScriptVerticalWritingMode(state: ManuscriptState): boolean;
  contentLoadState(state: ManuscriptState): "idle" | "loading" | "loaded";
};

type Actions = {
  initialize: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: InitializeManuscriptPayload
  ) => any;
  createManuscript: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: CreateManuscriptPayload
  ) => any;
  updateManuscript: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: UpdateManuscriptPayload
  ) => any;
  /** 校正反映など、適用済み本文を private S3 へ put し Vuex 本文も同期する */
  syncManuscriptContents: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: SyncManuscriptContentsPayload
  ) => Promise<void>;
  deleteManuscript: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: DeleteManuscriptPayload
  ) => any;
  initializeCreatedManuscriptKey: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  selectTheme: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: Theme) => any;
  selectCountType: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: CountType) => any;
  switchPreviewOnManuscriptEditor: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  updateShowFlags: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: ShowFlags) => any;
  updateScrollRatio: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: number) => any;
  toggleAutoSave: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  toggleShowEditorController: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  showAutoIndentMessage: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  closeAutoIndentMessage: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  toggleAutoIndent: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  toggleAssistFab: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  toggleEnabledSearchReplace: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  toggleEnabledSearch: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  toggleEnabledReplace: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  toggleEnabledReplaceSingle: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  toggleEnabledReplaceAll: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  updateSearchText: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: string) => any;
  updateReplaceText: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: string) => any;
  updateSearchTargetIndex: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: number) => any;
  updateSearchTotalCount: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: number) => any;
  updateSearchTextIndexList: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: SearchTextIndex[]
  ) => any;
  incrementWritingCount: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: IncrementWritingCountPayload
  ) => any;
  restoreVersion: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: RestoreVersionPayload
  ) => Promise<boolean>;
  fetchVersionContent: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: RestoreVersionPayload
  ) => any;
  initializeRestoredContent: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  updateOrder: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: UpdateOrder) => Promise<void>;
  toggleProgressPercentage: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  createFolder: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: CreateManuscriptFolder
  ) => Promise<void>;
  updateFolder: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: UpdateManuscriptFolder
  ) => Promise<void>;
  /** 優先取得: 単一原稿の本文を取得する */
  loadManuscriptContent: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: LoadManuscriptContentPayload
  ) => Promise<void>;
  /** バックグラウンド取得: 未取得の原稿本文を同時実行数を制限して逐次取得する */
  loadAllManuscriptContents: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: LoadAllManuscriptContentsPayload
  ) => Promise<void>;
  /** 作品切替・画面離脱時にバックグラウンド取得を中断する */
  cancelContentLoading: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>) => any;
  /** 原稿が編集中・未保存であることを登録し、バックグラウンド取得での上書きを防ぐ */
  registerEditingManuscript: (this: Store<{}>, injectee: ActionContext<ManuscriptState, {}>, payload: string) => any;
  unregisterEditingManuscript: (
    this: Store<{}>,
    injectee: ActionContext<ManuscriptState, {}>,
    payload: string
  ) => any;
};

interface InitializeManuscriptPayload {
  novelId: string;
  callback: () => void;
  /**
   * 本文のバックグラウンド一括取得まで行うか（デフォルト true）。
   * 「原稿が存在するか」の確認だけが目的の呼び出し元（AgentCampaignNovelListItem.vue等）が
   * 選択作品の全原稿本文を不要に取得してしまうのを避けるため false を渡せるようにしている。
   */
  loadContents?: boolean;
}

interface CreateManuscriptPayload {
  novelId: string;
  title: string;
  content?: string;
  manuscriptFolderId?: string;
  callback: () => void;
}

type CreateMultipleManuscriptPayload = {
  params: Omit<CreateManuscriptPayload, "callback">[];
  callback: () => void;
};

interface UpdateManuscriptPayload {
  novelId: string;
  key: string;
  title: string;
  content: string;
  callback: () => void | Promise<void>;
}

interface SyncManuscriptContentUpdate {
  key: string;
  content: string;
  lastModified?: string;
}

interface SyncManuscriptContentsPayload {
  updates: SyncManuscriptContentUpdate[];
}

interface DeleteManuscriptPayload {
  novelId: string;
  key: string;
  callback?: () => void;
}

interface IncrementWritingCountPayload {
  novelId: string;
  value: number;
}

interface RestoreVersionPayload {
  novelId: string;
  manuscriptKey: string;
  versionId: string;
}

interface LoadManuscriptContentPayload {
  novelId: string;
  key: string;
}

interface LoadAllManuscriptContentsPayload {
  novelId: string;
}

const mutations: MutationTree<ManuscriptState> = {
  setManuscriptList(state, payload) {
    return (state.manuscriptList = payload);
  },
  pushManuscript(state, payload) {
    state.manuscriptList.push(payload);
    return state.manuscriptList;
  },
  updateManuscript(state, payload: Manuscript) {
    return (state.manuscriptList = state.manuscriptList.map((manuscript) => {
      if (manuscript.key === payload.key) {
        return payload;
      }
      return manuscript;
    }));
  },
  popManuscript(state, payload) {
    const manuscriptList = state.manuscriptList.filter((manuscript) => manuscript.key !== payload);
    return (state.manuscriptList = manuscriptList);
  },
  setCreatedManuscriptKey(state, payload) {
    return (state.createdManuscriptKey = payload);
  },
  setTheme(state, payload: Theme) {
    return (state.theme = payload);
  },
  setCountType(state, payload: CountType) {
    return (state.countType = payload);
  },
  switchIsShowPreviewOnManuscriptEditor(state) {
    return (state.isShowPreviewOnManuscriptEditor = !state.isShowPreviewOnManuscriptEditor);
  },
  setShowFlags(state, payload: ShowFlags) {
    return (state.showFlags = payload);
  },
  setScrollRatio(state, payload) {
    return (state.scrollRatio = payload);
  },
  setAutoSave(state, payload) {
    return (state.autoSave = payload);
  },
  setAutoIndent(state, payload) {
    return (state.autoIndent = payload);
  },
  setFirstTimeAutoIndent(state, payload) {
    return (state.isFirstTimeAutoIndent = payload);
  },
  setShowingAutoIndentFirstTimeMessage(state, payload) {
    return (state.isShowingAutoIndentFirstTimeMessage = payload);
  },
  setShowEditorController(state, payload) {
    return (state.isShowEditorController = payload);
  },
  setEnabledSearchReplace(state, payload: boolean) {
    return (state.enabledSearchReplace = payload);
  },
  setShowAssistFab(state, payload: boolean) {
    return (state.isShowAssistFab = payload);
  },
  setEnabledSearch(state, payload: boolean) {
    return (state.enabledSearch = payload);
  },
  setEnabledReplace(state, payload: boolean) {
    return (state.enabledReplace = payload);
  },
  setEnabledReplaceSingle(state, payload: boolean) {
    return (state.enabledReplaceSingle = payload);
  },
  setEnabledReplaceAll(state, payload: boolean) {
    return (state.enabledReplaceAll = payload);
  },
  setSearchText(state, payload: string) {
    return (state.searchText = payload);
  },
  setReplaceText(state, payload: string) {
    return (state.replaceText = payload);
  },
  setSearchTargetIndex(state, payload: number) {
    return (state.searchTargetIndex = payload);
  },
  setSearchTotalCount(state, payload: number) {
    return (state.searchTotalCount = payload);
  },
  setSearchTextIndexList(state, payload: SearchTextIndex[]) {
    return (state.searchTextIndexList = payload);
  },
  setBaseWordCount(state, payload: number) {
    return (state.baseWordCount = payload);
  },
  setRestoredContent(state, payload: string | null) {
    return (state.restoredContent = payload);
  },
  setProgressPercentage(state, payload) {
    return (state.progressPercentage = payload);
  },
  setManuscriptFolderList(state, payload) {
    return (state.manuscriptFolderList = payload);
  },
  pushManuscriptFolder(state, payload) {
    state.manuscriptFolderList.push(payload);
    return state.manuscriptFolderList;
  },
  setExportCompleted(state, payload) {
    state.isExportCompleted = payload;
  },
  setExportCancelled(state, payload) {
    state.isExportCancelled = payload;
  },
  setManuscriptSaved(state, payload) {
    state.isSaved = payload;
  },
  setWordLimitErrorDialogShown(state, payload) {
    state.hasShownWordLimitErrorDialog = payload;
  },
  setVerticalWritingMode(state, payload: boolean) {
    state.isVerticalWritingMode = payload;
  },
  setScriptVerticalWritingMode(state, payload: boolean) {
    state.isScriptVerticalWritingMode = payload;
  },
  setManuscriptContent(
    state,
    payload: { key: string; content: string; contentStatus: ManuscriptContentStatus }
  ) {
    let found = false;
    state.manuscriptList = state.manuscriptList.map((manuscript) => {
      if (manuscript.key !== payload.key) return manuscript;
      found = true;
      return {
        ...manuscript,
        content: payload.content,
        contentLoaded: true,
        contentStatus: payload.contentStatus,
      };
    });
    if (!found) {
      // initialize によるリスト全消去と取得完了のタイミングが重なった場合等、
      // manuscriptList に対象キーが無いと取得済み本文が黙って破棄されてしまうため警告する
      console.warn(`setManuscriptContent: key not found in manuscriptList. Key: ${payload.key}`);
    }
  },
  setContentLoadState(state, payload: "idle" | "loading" | "loaded") {
    state.contentLoadState = payload;
  },
  incrementLoadGeneration(state) {
    state.loadGeneration += 1;
  },
  addEditingManuscriptKey(state, key: string) {
    if (!state.editingManuscriptKeys.includes(key)) {
      state.editingManuscriptKeys = [...state.editingManuscriptKeys, key];
    }
  },
  removeEditingManuscriptKey(state, key: string) {
    state.editingManuscriptKeys = state.editingManuscriptKeys.filter((k) => k !== key);
  },
};

const actions: ActionTree<ManuscriptState, {}> & Actions = {
  async initialize({ commit, dispatch, state }, payload: InitializeManuscriptPayload) {
    // 編集中・未保存の原稿は、直後の一覧再構築で本文を失わないよう直前のstateから引き継ぐ
    // （editingManuscriptKeysはExample: 執筆画面で開いたまま一覧の並べ替えを終了した場合等に登録されている）
    const editingKeys = new Set(state.editingManuscriptKeys);
    const previousManuscriptsByKey = new Map(
      state.manuscriptList.filter((m): m is Manuscript & { key: string } => !!m.key).map((m) => [m.key, m])
    );

    /** 初期化: メタ情報のみを取得する。本文は別途 loadManuscriptContent / loadAllManuscriptContents で取得する */
    commit("setManuscriptList", []);
    commit("incrementLoadGeneration");
    commit("setContentLoadState", "idle");

    const { novelId, callback, loadContents = true } = payload;

    const manuscriptFolderList = await manuscriptClient.fetchAllManuscriptFolder(novelId);
    commit("setManuscriptFolderList", manuscriptFolderList);

    const manuscriptList = await manuscriptClient.fetchAllManuscript(novelId);
    const metadataList = await manuscriptClient.fetchManuscriptMetadataList(novelId);
    const lastModifiedByKey = new Map(metadataList.map((item) => [item.manuscriptKey, item.lastModified]));

    const promiseItems = manuscriptList.map(async (manuscript) => {
      const { key } = manuscript;
      if (!key) {
        return { ...manuscript, novelId, content: "", contentLoaded: false };
      }

      // バージョン一覧は軽量なメタ情報のため、本文取得とは切り離して従来通り初期化時に取得する
      const versions = await manuscriptClient.fetchManuscriptVersions(novelId, key);

      const lastModifiedMs = lastModifiedByKey.get(key);
      const lastModified = lastModifiedMs ? format(new Date(lastModifiedMs), "YYYY/MM/DD HH:mm:ss") : "";

      if (editingKeys.has(key)) {
        const previous = previousManuscriptsByKey.get(key);
        if (previous) {
          return {
            ...manuscript,
            novelId,
            content: previous.content,
            contentLoaded: previous.contentLoaded,
            contentStatus: previous.contentStatus,
            lastModified,
            versions,
          };
        }
      }

      return { ...manuscript, novelId, content: "", contentLoaded: false, lastModified, versions };
    });
    const manuscripts = await Promise.all(promiseItems);

    const displayOrder = await displayOrderClient.fetchDisplayOrder({ novelId, kind: SharedLinkKind.MANUSCRIPT });

    let sortedManuscripts = manuscripts;
    if (displayOrder.order.length > 0) {
      // displayOrderに従っているmanuscriptsをソート
      const orderedManuscripts = manuscripts
        .filter((m) => m.key !== undefined && displayOrder.order.includes(m.key))
        .sort((a, b) => displayOrder.order.indexOf(a.key!) - displayOrder.order.indexOf(b.key!));

      // displayOrderに存在しないキーを持つmanuscriptsを特定
      const unorderedManuscripts = manuscripts.filter(
        (m) => m.key !== undefined && !displayOrder.order.includes(m.key)
      );

      // 両方の配列を結合
      sortedManuscripts = [...orderedManuscripts, ...unorderedManuscripts];
    }

    commit("setManuscriptList", sortedManuscripts);

    if (callback) callback();

    if (loadContents) {
      // メタ情報表示後、本文はバックグラウンドで取得する（優先取得したい原稿があれば、
      // 呼び出し元が別途 loadManuscriptContent を先に呼ぶことでこのループより先に取得できる）
      dispatch("loadAllManuscriptContents", { novelId });
    }
  },
  async loadManuscriptContent({ commit, state }, payload: LoadManuscriptContentPayload) {
    const { novelId, key } = payload;
    if (state.editingManuscriptKeys.includes(key)) return;

    const alreadyLoaded = state.manuscriptList.find((m) => m.key === key)?.contentLoaded;
    if (alreadyLoaded) return;

    // 優先取得とバックグラウンド一括取得が同じキーに対して同時に呼ばれた場合、
    // 2つ目以降は新規リクエストを発行せず、進行中のリクエストの完了を待つ。
    // novelIdも含めてキー化することで、作品間でmanuscriptKeyが重複した場合の誤共有を避ける。
    const inFlightKey = `${novelId}:${key}`;
    const inFlight = inFlightContentLoads.get(inFlightKey);
    if (inFlight) {
      await inFlight;
      return;
    }

    const fetchPromise = (async () => {
      let result: ManuscriptContentPayload;
      try {
        try {
          result = await manuscriptClient.fetchManuscriptContentResult(novelId, key);
        } catch (error) {
          // 画面初期表示直後は他の初期化リクエストと重なりやすく、一時的な失敗が起こり得るため1回だけ再試行する
          console.warn(`Manuscript content fetch failed, retrying once. Key: ${key}`, error);
          result = await manuscriptClient.fetchManuscriptContentResult(novelId, key);
        }
      } catch (error) {
        // リトライも失敗した場合、ここで例外を投げずクライアント側フォールバックの
        // ステータス（FETCH_ERROR）でcommitする。これにより loadManuscriptContent は
        // 呼び出し元（優先取得/バックグラウンドワーカー）に対して常に正常解決し、
        // contentLoaded: true になるため一覧の警告表示や全文依存機能の完了待ちも正しく進む。
        console.warn(`Manuscript content fetch failed after retry. Key: ${key}`, error);
        if (state.editingManuscriptKeys.includes(key)) return;
        commit("setManuscriptContent", { key, content: "", contentStatus: "FETCH_ERROR" });
        return;
      }
      if (state.editingManuscriptKeys.includes(key)) return;

      commit("setManuscriptContent", {
        key,
        content: result.status === "PRESENT" ? result.text ?? "" : "",
        contentStatus: result.status,
      });
    })();

    inFlightContentLoads.set(inFlightKey, fetchPromise);
    try {
      await fetchPromise;
    } finally {
      inFlightContentLoads.delete(inFlightKey);
    }
  },
  async loadAllManuscriptContents({ commit, state, dispatch }, payload: LoadAllManuscriptContentsPayload) {
    const { novelId } = payload;
    const generation = state.loadGeneration;
    const targets = state.manuscriptList.filter((m): m is Manuscript & { key: string } => !!m.key && !m.contentLoaded);

    if (targets.length === 0) {
      commit("setContentLoadState", "loaded");
      return;
    }

    commit("setContentLoadState", "loading");

    let index = 0;
    const worker = async () => {
      while (index < targets.length) {
        if (state.loadGeneration !== generation) return;
        const manuscript = targets[index];
        index += 1;

        // NOTE: ワーカーごとに逐次取得することで、ワーカー数（BACKGROUND_LOAD_CONCURRENCY）を
        // 同時実行数の上限として機能させている。意図的な逐次await。
        try {
          // eslint-disable-next-line no-await-in-loop
          await dispatch("loadManuscriptContent", { novelId, key: manuscript.key });
        } catch (error) {
          // 1件の取得失敗でバックグラウンド取得全体を止めないよう、ログのみ残し次の原稿へ進む
          console.warn(`Background manuscript content load failed. Key: ${manuscript.key}`, error);
        }
        if (state.loadGeneration !== generation) return;
      }
    };

    const workerCount = Math.min(BACKGROUND_LOAD_CONCURRENCY, targets.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    if (state.loadGeneration === generation) {
      commit("setContentLoadState", "loaded");
    }
  },
  cancelContentLoading({ commit }) {
    commit("incrementLoadGeneration");
    // ワーカーは generation 不一致で早期returnし、setContentLoadState("loaded") も
    // generation一致時のみ実行されるため、ここでリセットしないと "loading" のまま
    // 永続してしまい、以降 ensureAllManuscriptContentsLoaded の完了待ちが永久に解決しない。
    commit("setContentLoadState", "idle");
  },
  registerEditingManuscript({ commit }, key: string) {
    commit("addEditingManuscriptKey", key);
  },
  unregisterEditingManuscript({ commit }, key: string) {
    commit("removeEditingManuscriptKey", key);
  },
  async createManuscript({ commit }, payload: CreateManuscriptPayload) {
    const { novelId, title, content = "", callback } = payload;

    const key = await manuscriptClient.createManuscript(payload);
    // createManuscriptはメタ情報（key/title）のみを保存するため、本文は別途保存する
    const { savedAt } = await manuscriptClient.saveManuscriptContent(novelId, key, content);
    const lastModified = format(new Date(savedAt), "YYYY/MM/DD HH:mm:ss");

    // 原稿のバージョンを取得
    const versions = await manuscriptClient.fetchManuscriptVersions(novelId, key);

    commit("pushManuscript", {
      key,
      title,
      novelId,
      content,
      lastModified,
      versions,
      contentLoaded: true,
      contentStatus: "PRESENT",
    } as Manuscript);
    commit("setCreatedManuscriptKey", key);
    if (callback) callback();
  },
  async createMultipleManuscript({ commit }, payload: CreateMultipleManuscriptPayload) {
    const { params, callback } = payload;
    const payloads = params.map((item) => {
      const { novelId, title, content = "", manuscriptFolderId } = item;
      return { novelId, key: uuidv4(), title, content, manuscriptFolderId };
    });

    await manuscriptClient.createMultipleManuscript(payloads);

    // saveManuscriptContentはnovel単位の1レコード（Manuscript.items）をget→map→updateするため、
    // 同一novelへ並列実行するとlastModifiedの更新がロストアップデートで失われ得る。
    // バックエンドに複数原稿一括保存APIが用意されるまでは、直列実行でこれを確実に避ける。
    await mapWithConcurrency(payloads, 1, async (manuscript) => {
      const { novelId, key, title, content } = manuscript;

      // createMultipleManuscriptもメタ情報のみを保存するため、本文は別途保存する
      const { savedAt } = await manuscriptClient.saveManuscriptContent(novelId, key, content);
      const lastModified = format(new Date(savedAt), "YYYY/MM/DD HH:mm:ss");

      // 原稿のバージョンを取得
      const versions = await manuscriptClient.fetchManuscriptVersions(novelId, key);

      commit("pushManuscript", {
        key,
        title,
        novelId,
        content,
        lastModified,
        versions,
        contentLoaded: true,
        contentStatus: "PRESENT",
      } as Manuscript);
    });

    if (callback) callback();
  },
  /**
   * 校正の確定適用後、Vuex 上の原稿本文をレスポンス内容で同期する。
   * `/proofreading/apply` が既にサーバー側（S3）へ保存済みのため、ここでの追加保存は不要。
   */
  async syncManuscriptContents({ state, commit }, payload: SyncManuscriptContentsPayload) {
    const { updates } = payload;
    updates.forEach((update) => {
      const manuscript = state.manuscriptList.find((item) => item.key === update.key);
      if (!manuscript) return;

      commit("updateManuscript", {
        ...manuscript,
        content: update.content,
        ...(update.lastModified !== undefined ? { lastModified: update.lastModified } : {}),
      } as Manuscript);
    });
  },
  async updateManuscript({ commit, dispatch, rootState }, payload: UpdateManuscriptPayload) {
    const { novelId, key, callback } = payload;

    // 不変条件: 永続化される本文（サーバー / IndexedDB / S3）にソフト改行マーカー(U+2060)を含めない。
    // 縦書き脚本の save() は unreflow 済みだが、unreflow を経ない経路（マルチ執筆など）でも
    // マーカーが混入しないよう、全保存経路が通るこのアクションで最終的に除去する。
    const content = stripSoftBreakMarker(payload.content);
    const sanitizedPayload = { ...payload, content };

    // 本文が主データのため、メタ更新より先に保存する。後段（メタ更新）が失敗しても
    // 本文自体は保存済みの状態を維持でき、失敗時に残る不整合を小さくできる。
    const { savedAt } = await manuscriptClient.saveManuscriptContent(novelId, key, content);
    const lastModified = format(new Date(savedAt), "YYYY/MM/DD HH:mm:ss");
    const manuscriptList = await manuscriptClient.updateManuscript(sanitizedPayload);

    // 原稿のバージョンを取得
    const versions = await manuscriptClient.fetchManuscriptVersions(novelId, key);

    // NOTE: 共有リンク閲覧時の本文取得（sharedManuscriptContent）はオーナーのprivate本文を
    // 直接読む方式になっており、protected領域への複製は不要になったため呼び出しを削除した。

    const updatedManuscript = manuscriptList.find((manuscript) => manuscript.key === payload.key);

    if (updatedManuscript) {
      commit("updateManuscript", {
        ...updatedManuscript,
        content,
        lastModified,
        versions,
        // 保存が成功した直後なので明示する。updatedManuscriptはサーバーのメタ情報のみで
        // これらのフィールドを持たないため、指定しないとundefinedに戻ってしまう。
        contentLoaded: true,
        contentStatus: "PRESENT",
      } as Manuscript);

      // 🔧 アシスト機能の原稿データも同期更新
      const assistSelectedNovelId = (rootState as any).assistSelectManuscriptModule?.selectedNovelId;
      if (assistSelectedNovelId === novelId) {
        try {
          // 保存完了後に通常の再取得を実行（ローディング表示あり）
          await dispatch("assistSelectManuscriptModule/fetchManuscriptData", novelId, { root: true });
          console.log("Assist manuscript data synced after save");
        } catch (error) {
          console.warn("Failed to sync assist manuscript data:", error);
        }
      }

      if (callback) await callback();
    }
  },
  async deleteManuscript({ commit, dispatch, rootState }, payload: DeleteManuscriptPayload) {
    const { novelId, key, callback } = payload;

    await manuscriptClient.deleteManuscript(payload);

    commit("popManuscript", key);

    // 🔧 アシスト機能の原稿データも同期更新
    const assistSelectedNovelId = (rootState as any).assistSelectManuscriptModule?.selectedNovelId;
    if (assistSelectedNovelId === novelId) {
      try {
        // 削除完了後に通常の再取得を実行（ローディング表示あり）
        await dispatch("assistSelectManuscriptModule/fetchManuscriptData", novelId, { root: true });
        console.log("Assist manuscript data synced after deletion");
      } catch (error) {
        console.warn("Failed to sync assist manuscript data after deletion:", error);
      }
    }

    if (callback) callback();
  },
  initializeCreatedManuscriptKey({ commit }) {
    commit("setCreatedManuscriptKey", "");
  },
  async selectTheme({ commit }, value) {
    commit("setTheme", value);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(value), "theme");
    });
  },
  async selectCountType({ commit }, value) {
    commit("setCountType", value);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(value), "countType");
    });
  },
  switchPreviewOnManuscriptEditor({ commit }) {
    commit("switchIsShowPreviewOnManuscriptEditor");
  },
  updateShowFlags({ commit }, value) {
    commit("setShowFlags", value);
  },
  updateScrollRatio({ commit }, value) {
    commit("setScrollRatio", value);
  },
  async initializeGeneralSettings({ commit }) {
    const themeInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("theme")
    );
    if (themeInIndexedDB) {
      commit("setTheme", themeInIndexedDB.value);
    }

    const countTypeInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("countType")
    );
    if (countTypeInIndexedDB) {
      commit("setCountType", countTypeInIndexedDB.value);
    }

    const autoSaveInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("autoSave")
    );
    if (autoSaveInIndexedDB) {
      commit("setAutoSave", autoSaveInIndexedDB.value);
    }

    const autoIndentInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("autoIndent")
    );
    if (autoIndentInIndexedDB) {
      commit("setAutoIndent", autoIndentInIndexedDB.value);
    }
    commit("setFirstTimeAutoIndent", !autoIndentInIndexedDB);

    const isShowEditorControllerInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("isShowEditorController")
    );
    if (isShowEditorControllerInIndexedDB) {
      commit("setShowEditorController", isShowEditorControllerInIndexedDB.value);
    }

    const progressPercentageInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("progressPercentage")
    );
    if (progressPercentageInIndexedDB) {
      commit("setProgressPercentage", progressPercentageInIndexedDB.value);
    }

    const isShowAssistFabInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("isShowAssistFab")
    );
    if (isShowAssistFabInIndexedDB) {
      commit("setShowAssistFab", isShowAssistFabInIndexedDB.value);
    }

    const isVerticalWritingModeInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("isVerticalWritingMode")
    );
    if (isVerticalWritingModeInIndexedDB) {
      commit("setVerticalWritingMode", isVerticalWritingModeInIndexedDB.value);
    }

    const isScriptVerticalWritingModeInIndexedDB = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("isScriptVerticalWritingMode")
    );
    // 未設定（初回）の場合はデフォルト ON を維持する
    if (isScriptVerticalWritingModeInIndexedDB) {
      commit("setScriptVerticalWritingMode", isScriptVerticalWritingModeInIndexedDB.value);
    }
  },
  async toggleAutoSave({ commit, state }) {
    const { autoSave } = state;
    commit("setAutoSave", !autoSave);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(!autoSave), "autoSave");
    });
  },
  async toggleShowEditorController({ commit, state }) {
    const { isShowEditorController } = state;
    commit("setShowEditorController", !isShowEditorController);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(!isShowEditorController), "isShowEditorController");
    });
  },
  async toggleAutoIndent({ commit, state }) {
    const { autoIndent } = state;
    commit("setAutoIndent", !autoIndent);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(!autoIndent), "autoIndent");
    });
  },
  async toggleAssistFab({ commit, state }) {
    const { isShowAssistFab } = state;
    commit("setShowAssistFab", !isShowAssistFab);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(!isShowAssistFab), "isShowAssistFab");
    });
  },
  async toggleVerticalWritingMode({ commit, state }) {
    const { isVerticalWritingMode } = state;
    commit("setVerticalWritingMode", !isVerticalWritingMode);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(!isVerticalWritingMode), "isVerticalWritingMode");
    });
  },
  async toggleScriptVerticalWritingMode({ commit, state }) {
    const { isScriptVerticalWritingMode } = state;
    commit("setScriptVerticalWritingMode", !isScriptVerticalWritingMode);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(
        new GeneralSettingsDexie(!isScriptVerticalWritingMode),
        "isScriptVerticalWritingMode"
      );
    });
  },
  showAutoIndentMessage({ commit }) {
    commit("setShowingAutoIndentFirstTimeMessage", true);
  },
  async closeAutoIndentMessage({ commit }) {
    commit("setShowingAutoIndentFirstTimeMessage", false);
    commit("setFirstTimeAutoIndent", false);

    await db.transaction("readwrite", db.generalSettings, async () => {
      const record = await db.generalSettings.get("autoIndent");
      if (!record) {
        await db.generalSettings.put(new GeneralSettingsDexie(true), "autoIndent");
      }
    });
  },
  toggleEnabledSearchReplace({ commit, state }) {
    const { enabledSearchReplace } = state;
    commit("setEnabledSearchReplace", !enabledSearchReplace);
  },
  toggleEnabledSearch({ commit, state }) {
    const { enabledSearch } = state;
    commit("setEnabledSearch", !enabledSearch);
  },
  toggleEnabledReplace({ commit, state }) {
    const { enabledReplace } = state;
    commit("setEnabledReplace", !enabledReplace);
  },
  toggleEnabledReplaceSingle({ commit, state }) {
    const { enabledReplaceSingle } = state;
    commit("setEnabledReplaceSingle", !enabledReplaceSingle);
  },
  toggleEnabledReplaceAll({ commit, state }) {
    const { enabledReplaceAll } = state;
    commit("setEnabledReplaceAll", !enabledReplaceAll);
  },
  updateSearchText({ commit }, value) {
    commit("setSearchText", value);
  },
  updateReplaceText({ commit }, value) {
    commit("setReplaceText", value);
  },
  updateSearchTargetIndex({ commit }, value) {
    commit("setSearchTargetIndex", value);
  },
  updateSearchTotalCount({ commit }, value) {
    commit("setSearchTotalCount", value);
  },
  updateSearchTextIndexList({ commit }, value) {
    commit("setSearchTextIndexList", value);
  },
  async incrementWritingCount({ commit, state }, payload) {
    const { novelId, value } = payload;
    const { baseWordCount } = state;

    const recordWordCount = value - baseWordCount;
    if (recordWordCount > 0) await manuscriptClient.incrementWritingCount(novelId, recordWordCount);
    commit("setBaseWordCount", value);
  },
  async restoreVersion({ commit }, payload) {
    const { novelId, manuscriptKey, versionId } = payload;
    const result = await manuscriptClient.fetchManuscriptContentResult(novelId, manuscriptKey, versionId);

    if (result.status !== "PRESENT") return false;

    commit("setRestoredContent", result.text ?? "");
    return true;
  },
  async fetchVersionContent(_, payload) {
    const { novelId, manuscriptKey, versionId } = payload;
    return await manuscriptClient.fetchManuscriptContent(novelId, manuscriptKey, versionId);
  },
  initializeRestoredContent({ commit }) {
    commit("setRestoredContent", null);
  },
  async updateOrder(_, payload) {
    await displayOrderClient.updateDisplayOrder({
      ...payload,
      kind: SharedLinkKind.MANUSCRIPT,
    });
  },
  async toggleProgressPercentage({ commit, state }) {
    const { progressPercentage } = state;
    commit("setProgressPercentage", !progressPercentage);
    await db.transaction("readwrite", db.generalSettings, async () => {
      await db.generalSettings.put(new GeneralSettingsDexie(!progressPercentage), "progressPercentage");
    });
  },
  async createFolder({ commit }, payload) {
    const folder = await manuscriptClient.createManuscriptFolder(payload);
    commit("pushManuscriptFolder", { ...folder, manuscriptKeys: [] });
  },
  async updateFolder({ commit }, payload) {
    const folders = await manuscriptClient.updateManuscriptFolder(payload);
    commit("setManuscriptFolderList", folders);
  },
};

const getters: GetterTree<ManuscriptState, {}> & Getters = {
  manuscript: (state) => (id: string) => state.manuscriptList.find((manuscript) => manuscript.key === id),
  manuscriptList: (state) => state.manuscriptList,
  createdManuscriptKey: (state) => state.createdManuscriptKey,
  theme: (state) => state.theme,
  countType: (state) => state.countType,
  isShowPreviewOnManuscriptEditor: (state) => state.isShowPreviewOnManuscriptEditor,
  showFlags: (state) => state.showFlags,
  scrollRatio: (state) => state.scrollRatio,
  autoSave: (state) => state.autoSave,
  autoIndent: (state) => state.autoIndent,
  isShowingAutoIndentFirstTimeMessage: (state) => state.isShowingAutoIndentFirstTimeMessage,
  isFirstTimeAutoIndent: (state) => state.isFirstTimeAutoIndent,
  isShowEditorController: (state) => state.isShowEditorController,
  enabledSearchReplace: (state) => state.enabledSearchReplace,
  isShowAssistFab: (state) => state.isShowAssistFab,
  enabledSearch: (state) => state.enabledSearch,
  enabledReplace: (state) => state.enabledReplace,
  enabledReplaceSingle: (state) => state.enabledReplaceSingle,
  enabledReplaceAll: (state) => state.enabledReplaceAll,
  searchText: (state) => state.searchText,
  replaceText: (state) => state.replaceText,
  searchTargetIndex: (state) => state.searchTargetIndex,
  searchTotalCount: (state) => state.searchTotalCount,
  searchTextIndexList: (state) => state.searchTextIndexList,
  baseWordCount: (state) => state.baseWordCount,
  restoredContent: (state) => state.restoredContent,
  progressPercentage: (state) => state.progressPercentage,
  manuscriptFolder: (state) => (folderId: string) =>
    state.manuscriptFolderList.find((x) => x.manuscriptFolderId === folderId),
  manuscriptFolderList: (state) => state.manuscriptFolderList,
  foldersBelongManuscript: (state) => (manuscriptKey: string) =>
    state.manuscriptFolderList.filter((x) => x.manuscriptKeys.includes(manuscriptKey)),
  isExportCompleted: (state) => state.isExportCompleted,
  isExportCancelled: (state) => state.isExportCancelled,
  isSaved: (state) => state.isSaved,
  hasShownWordLimitErrorDialog: (state) => state.hasShownWordLimitErrorDialog,
  isVerticalWritingMode: (state) => state.isVerticalWritingMode,
  isScriptVerticalWritingMode: (state) => state.isScriptVerticalWritingMode,
  contentLoadState: (state) => state.contentLoadState,
};

export default {
  namespaced: true,
  state: {
    manuscriptList: [],
    createdManuscriptKey: "",
    theme: "default",
    countType: "onlyCharacter",
    isShowPreviewOnManuscriptEditor: false,
    showFlags: {
      isShowPlots: true,
      isShowCharacters: true,
      isShowWorldViews: true,
      isShowMemo: false,
    },
    scrollRatio: 0,
    autoSave: false,
    autoIndent: true,
    isFirstTimeAutoIndent: false,
    isShowingAutoIndentFirstTimeMessage: false,
    isShowEditorController: true,
    enabledSearchReplace: false,
    isShowAssistFab: true,
    enabledSearch: false,
    enabledReplace: false,
    enabledReplaceSingle: false,
    enabledReplaceAll: false,
    searchText: "",
    replaceText: "",
    searchTargetIndex: 0,
    searchTotalCount: 0,
    searchTextIndexList: [],
    baseWordCount: 0,
    restoredContent: null,
    progressPercentage: false,
    manuscriptFolderList: [],
    isExportCompleted: false,
    isExportCancelled: false,
    isSaved: true,
    hasShownWordLimitErrorDialog: false,
    isVerticalWritingMode: false,
    isScriptVerticalWritingMode: true,
    contentLoadState: "idle",
    loadGeneration: 0,
    editingManuscriptKeys: [],
  },
  getters,
  actions,
  mutations,
};
